import { LOCATION_INTEL_PAGE_MAX, type LocationIntelConfigDto, type LocationIntelPointsPageDto } from '@pams/shared';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'antd';
import { Info, MapPinned } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Loading, Skeleton } from '@/components/Skeleton';
import { istTime } from '@/lib/analyticsReport';
import { ApiError, api } from '@/lib/api';
import {
  LOCATION_INTEL_CONFIG_PATH,
  LOCATION_INTEL_POINTS_PATH,
  countByBand,
  readAllPoints,
} from '@/lib/locationIntel';
import { queryKeys } from '@/lib/queryKeys';
import { DASHBOARD_PALETTE, useTheme } from '@/theme/ThemeProvider';
import { uiPalettes } from '@/theme/tokens';
import { LocationMap } from './components/LocationMap';
import { MapKey } from './components/MapKey';
import { OfficerCard } from './components/OfficerCard';
import { LOCATION_INTEL_COPY } from './copy';
import { BASEMAPS, defaultBasemapFor, mapColoursFrom } from './mapSpec';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL] LOCATIONAL INTEL — where the officers in the holder's area last reported a location.
 *
 *  ⚠️ THE ROUTE GATE IS NOT THE CONTROL. `/location-intel` sits behind `RequireCapability` with
 *  `LOCATION_INTEL_ACCESS`, which only stops a page opening for somebody every request behind it refuses;
 *  the three API routes refuse a non-holder themselves, and confine a holder to their own scope.
 *
 *  Three reads, in order:
 *    1. THE CONFIG — the map's public token, or `null`. `null` is a page that says the map is not
 *       configured, and the map library is then never fetched at all.
 *    2. THE POINTS — every page of them (`readAllPoints`): an id, a position and a band per officer, and
 *       NOTHING ELSE. No name, rank or phone reaches the browser until a dot is selected.
 *    3. ONE OFFICER — on a click, in `OfficerCard`. Never a phone.
 *
 *  ⚠️ [LOCATION-INTEL.1] THE MAP TAKES THE WHOLE CONTENT AREA. The page is a column exactly as tall as `<main>`'s
 *  content box (`h-full`, which resolves because every flex ancestor up to the shell has a definite height); the
 *  header takes its own height and the map the rest (`flex-1`), down to a floor below which `<main>` scrolls. The
 *  side panel became the Map key dialog, by the user's instruction; the counts stay beside the map as visually
 *  hidden text, because a canvas says nothing to a screen reader.
 *
 *  ⚠️ THE BASEMAP IS URL STATE (`?basemap=`), so a chosen view survives a reload and travels with a link — and its
 *  absence means "follow the dashboard's theme" (`defaultBasemapFor`), so no choice is stored for anybody who never
 *  made one.
 *
 *  ⚠️ NO AUTOMATIC REFRESH. A band is decided on the server at the moment it answers, so the header states when
 *  that was and offers Refresh; a timer would be a request a minute from every open tab, for a map whose meaning
 *  is "last reported", not "live".
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = LOCATION_INTEL_COPY;

const refusalOf = (error: unknown): string | null =>
  error instanceof ApiError && error.status >= 400 && error.status < 500 ? error.message : null;

const LocationIntelPage = () => {
  const { mode } = useTheme();
  const colours = useMemo(() => mapColoursFrom(uiPalettes[DASHBOARD_PALETTE][mode]), [mode]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapKeyOpen, setMapKeyOpen] = useState(false);
  const [chosenBasemap, setChosenBasemap] = useQueryState(
    'basemap',
    parseAsStringLiteral(BASEMAPS).withOptions({ history: 'replace' }),
  );
  const basemap = chosenBasemap ?? defaultBasemapFor(mode);

  const config = useQuery({
    queryKey: queryKeys.locationIntel.config(),
    queryFn: ({ signal }) => api.get<LocationIntelConfigDto>(LOCATION_INTEL_CONFIG_PATH, { signal }),
  });
  const token = config.data?.mapboxAccessToken ?? null;

  const points = useQuery({
    queryKey: queryKeys.locationIntel.points(),
    queryFn: ({ signal }) =>
      readAllPoints((page) =>
        api.get<LocationIntelPointsPageDto>(LOCATION_INTEL_POINTS_PATH, {
          query: { page, limit: LOCATION_INTEL_PAGE_MAX },
          signal,
        }),
      ),
    /* ⚠️ No token, no map — and no reason to read the force's positions into a page that cannot draw them. */
    enabled: token !== null,
  });

  const counts = useMemo(() => countByBand(points.data?.points ?? []), [points.data]);
  const summary = points.data === undefined ? copy.loadingPoints : copy.summary(points.data.total, counts);
  /* ⚠️ [LOCATION-INTEL.1] Short of the count, in the words of its cause: the page bound, or a list that moved mid-read. */
  const truncated =
    points.data !== undefined && points.data.points.length < points.data.total
      ? (points.data.capped ? copy.truncated : copy.shortRead)(points.data.points.length, points.data.total)
      : null;
  const readAt = points.data === undefined ? null : istTime(new Date(points.dataUpdatedAt).toISOString());
  /*
   * ⚠️ [LOCATION-INTEL.1] A FAILED BACKGROUND REFETCH IS NOT A FAILED READ. TanStack v5 sets `isError` when a refetch
   * fails and KEEPS the data (row 1206's rule), so a config re-read lost to a network blip on reconnect used to swap a
   * working map for "The map could not load" and drop the viewer's view. Only a read with nothing to show is a failure
   * here — the points query has always been asked that way.
   * ⚠️ A REFUSED RE-READ (a 403, the flag revoked mid-session) KEEPS THE LAST READ TOO, as Usage and Duty analytics do:
   * the server refuses every new read, the Map key says the server's sentence, and a reload shows the refusal itself.
   */
  const configFailed = config.isError && config.data === undefined;
  const pointsFailed = points.isError && points.data === undefined;
  const mapShown = !config.isPending && !configFailed && token !== null && !pointsFailed;
  /*
   * ⚠️ [LOCATION-INTEL.1] THE SIDE TEXT IS IN THE MAP KEY, BUT AN EMPTY-LOOKING MAP STILL SAYS WHY. While the points are
   * read, when nobody has reported, and when the map could not draw everybody, one line sits on the map — the states
   * a viewer would otherwise read as the "nothing showing" this page was reported for. The ordinary case shows nothing.
   */
  const notice =
    points.data === undefined ? copy.loadingPoints : points.data.total === 0 ? copy.noneReported : truncated;
  /* What a screen reader hears beside the map: the counts, and the truncation the Map key states. */
  const spoken = truncated === null ? summary : `${summary} ${truncated}`;

  return (
    <div className="flex h-full min-h-[560px] flex-col gap-4" data-testid="location-intel-root">
      <PageHeader
        icon={MapPinned}
        title={copy.title}
        subtitle={copy.subtitle}
        actions={
          !mapShown ? undefined : (
            <>
              {readAt !== null && (
                <span className="text-sm text-muted-foreground" data-testid="location-intel-updated-short">
                  {points.isError ? copy.refreshFailedShort : copy.updatedShort(readAt)}
                </span>
              )}
              <Button
                icon={<Info className="size-4" aria-hidden />}
                onClick={() => {
                  setMapKeyOpen(true);
                }}
                data-testid="location-intel-map-key-open"
              >
                {copy.mapKey}
              </Button>
              {/* ⚠️ `disabled`, never `loading`: a loading antd button accepts the click and drops it ([C3c §10]). */}
              <Button
                onClick={() => void points.refetch()}
                disabled={points.isFetching}
                data-testid="location-intel-refresh"
              >
                {copy.refresh}
              </Button>
            </>
          )
        }
      />

      {config.isPending ? (
        /* ⚠️ The bar FILLS the slot the map will take (a flex column all the way down), so the area never collapses
           while the config is read. The header's Map key and Refresh still arrive with the map, which then starts
           lower by the height they add: 10 px at 1366 × 768, about 50 px on a phone. */
        <div className="flex min-h-[420px] flex-1 flex-col" data-testid="location-intel-loading">
          <Loading label={copy.loading} className="flex flex-1 flex-col">
            <Skeleton className="min-h-[420px] w-full flex-1" />
          </Loading>
        </div>
      ) : configFailed ? (
        refusalOf(config.error) !== null ? (
          /* ⚠️ `EmptyState` forwards no `data-testid`, so the handle is a wrapper (the StatusPill lesson). */
          <div data-testid="location-intel-refused">
            <EmptyState title={refusalOf(config.error) ?? ''} />
          </div>
        ) : (
          <EmptyState
            title={copy.errorTitle}
            description={copy.errorDescription}
            action={<Button onClick={() => void config.refetch()}>{copy.retry}</Button>}
          />
        )
      ) : token === null ? (
        <div data-testid="location-intel-not-configured">
          <EmptyState title={copy.notConfiguredTitle} description={copy.notConfiguredDescription} />
        </div>
      ) : pointsFailed ? (
        refusalOf(points.error) !== null ? (
          <div data-testid="location-intel-refused">
            <EmptyState title={refusalOf(points.error) ?? ''} />
          </div>
        ) : (
          <div data-testid="location-intel-points-failed">
            <EmptyState
              title={copy.errorTitle}
              description={copy.errorDescription}
              action={<Button onClick={() => void points.refetch()}>{copy.retry}</Button>}
            />
          </div>
        )
      ) : (
        /* ⚠️ `relative`, so the visually hidden sentence below has a positioned ancestor and cannot extend the
           document ([D4]'s measured `sr-only` trap). `flex-1` gives the map every pixel the header leaves. */
        <div className="relative min-h-[420px] flex-1">
          <p className="sr-only" data-testid="location-intel-summary-sr">
            {spoken}
          </p>
          {/* ⚠️ Keyed on the token: a new token is a new map, so the map never has to reset itself. */}
          <LocationMap
            key={token}
            token={token}
            points={points.data?.points ?? []}
            colours={colours}
            basemap={basemap}
            notice={notice}
            onBasemapChange={(next) => void setChosenBasemap(next)}
            onSelect={setSelectedId}
          />
        </div>
      )}

      <MapKey
        open={mapKeyOpen}
        onClose={() => {
          setMapKeyOpen(false);
        }}
        colours={colours}
        summary={summary}
        truncated={truncated}
        /* A refused re-read says the server's own sentence: "Try Refresh again" would be untrue of a 403. */
        updated={
          readAt === null
            ? null
            : points.isError
              ? (refusalOf(points.error) ?? copy.refreshFailed)
              : copy.updatedAt(readAt)
        }
      />
      <OfficerCard
        officerId={selectedId}
        onClose={() => {
          setSelectedId(null);
        }}
      />
    </div>
  );
};

export default LocationIntelPage;
